import React from 'react'
import { Table, Space, Button, Pagination, Tag, Empty, Spin } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

export interface DataTableColumn<T = any> {
  key: string
  title: string
  dataIndex?: string | string[]
  render?: (value: any, record: T, index: number) => React.ReactNode
  width?: string | number
  align?: 'left' | 'center' | 'right'
  sorter?: boolean | ((a: T, b: T) => number)
  filters?: Array<{ text: string; value: string | number }>
  ellipsis?: boolean
}

export interface DataTableAction<T = any> {
  key: string
  label: string
  icon?: React.ReactNode
  onClick?: (record: T) => void
  danger?: boolean
  confirm?: string
}

interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[]
  dataSource: T[]
  loading?: boolean
  pagination?: {
    current: number
    pageSize: number
    total: number
  }
  onPageChange?: (page: number) => void
  rowKey?: string | ((record: T) => string)
  actions?: DataTableAction<T>[]
  onActionClick?: (action: string, record: T) => void
  selectable?: boolean
  onSelectionChange?: (selectedKeys: string[]) => void
  bordered?: boolean
  striped?: boolean
  size?: 'large' | 'middle' | 'small'
  emptyText?: string
}

export const DataTable = React.forwardRef<any, DataTableProps>(
  (
    {
      columns,
      dataSource,
      loading = false,
      pagination,
      onPageChange,
      rowKey = 'id',
      actions = [],
      onActionClick,
      selectable = false,
      onSelectionChange,
      bordered = false,
      striped = true,
      size = 'middle',
      emptyText = 'No data available',
    },
    ref
  ) => {
    const tableColumns: ColumnsType<any> = [
      ...columns.map((col) => ({
        key: col.key,
        title: col.title,
        dataIndex: col.dataIndex || col.key,
        render: col.render,
        width: col.width,
        align: col.align as any,
        sorter: col.sorter,
        filters: col.filters,
        ellipsis: col.ellipsis !== false,
      })),
      ...(actions.length > 0
        ? [
            {
              key: 'actions',
              title: 'Actions',
              width: actions.length * 40 + 10,
              render: (_: any, record: any) => (
                <Space size="small">
                  {actions.map((action) => (
                    <Button
                      key={action.key}
                      type="text"
                      size="small"
                      icon={action.icon}
                      onClick={() => onActionClick?.(action.key, record)}
                      danger={action.danger}
                      title={action.label}
                    />
                  ))}
                </Space>
              ),
            },
          ]
        : []),
    ]

    const rowSelection = selectable
      ? {
          onChange: (selectedKeys: React.Key[]) => {
            onSelectionChange?.(selectedKeys as string[])
          },
        }
      : undefined

    return (
      <Spin spinning={loading}>
        <Table
          ref={ref}
          columns={tableColumns}
          dataSource={dataSource}
          rowKey={rowKey}
          pagination={
            pagination
              ? {
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  onChange: onPageChange,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }
              : false
          }
          rowSelection={rowSelection}
          bordered={bordered}
          size={size}
          locale={{
            emptyText: <Empty description={emptyText} />,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Spin>
    )
  }
)

DataTable.displayName = 'DataTable'

// Status Tag Component
interface StatusTagProps {
  status: string
  statusConfig?: Record<string, { color: string; label: string }>
}

export const StatusTag: React.FC<StatusTagProps> = ({
  status,
  statusConfig = {
    draft: { color: 'default', label: 'Draft' },
    submitted: { color: 'processing', label: 'Submitted' },
    approved: { color: 'success', label: 'Approved' },
    in_progress: { color: 'processing', label: 'In Progress' },
    ready: { color: 'warning', label: 'Ready' },
    shipped: { color: 'cyan', label: 'Shipped' },
    received: { color: 'success', label: 'Received' },
    completed: { color: 'success', label: 'Completed' },
    cancelled: { color: 'error', label: 'Cancelled' },
  },
}) => {
  const config = statusConfig[status] || { color: 'default', label: status }
  return <Tag color={config.color}>{config.label}</Tag>
}

// Filter Bar Component
interface FilterBarProps {
  filters: Array<{
    key: string
    label: string
    type: 'text' | 'select' | 'date'
    options?: Array<{ value: string; label: string }>
  }>
  onFilterChange: (filters: Record<string, any>) => void
}

export const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange }) => {
  const [filterValues, setFilterValues] = React.useState<Record<string, any>>({})

  const handleChange = (key: string, value: any) => {
    const newFilters = { ...filterValues, [key]: value }
    setFilterValues(newFilters)
    onFilterChange(newFilters)
  }

  return (
    <Space wrap style={{ marginBottom: '16px', width: '100%' }}>
      {filters.map((filter) => {
        if (filter.type === 'text') {
          return (
            <input
              key={filter.key}
              type="text"
              placeholder={filter.label}
              onChange={(e) => handleChange(filter.key, e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
            />
          )
        } else if (filter.type === 'select') {
          return (
            <select
              key={filter.key}
              onChange={(e) => handleChange(filter.key, e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
            >
              <option value="">{filter.label}</option>
              {filter.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )
        }
        return null
      })}
      <Button
        onClick={() => {
          setFilterValues({})
          onFilterChange({})
        }}
      >
        Clear
      </Button>
    </Space>
  )
}
